import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Badge } from "../ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "../ui/alert-dialog";
import {
  getProficiencyCatalog, createProficiencyIndicator, updateProficiencyIndicator,
  deleteProficiencyIndicator, getAssessmentCategoryOptions,
} from "../../lib/api";
import ProficiencyImportCard from "./ProficiencyImportCard";

const EMPTY_LINK = { category: "", min_score: 4, required_count: 3, group_type: "" };

export default function ProficiencyCatalogManager() {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [filterSubject, setFilterSubject] = useState("all");
  const [filterType, setFilterType] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const [cat, ac] = await Promise.all([getProficiencyCatalog(), getAssessmentCategoryOptions()]);
      setItems(cat.data.items || []);
      setCategories(ac.data.categories || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load catalog");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const subjects = useMemo(() => [...new Set(items.map((i) => i.subject))].sort(), [items]);

  const filtered = useMemo(() => items.filter(
    (i) => (filterSubject === "all" || i.subject === filterSubject)
        && (filterType === "all" || i.type === filterType),
  ), [items, filterSubject, filterType]);

  const handleDelete = async (id) => {
    try {
      await deleteProficiencyIndicator(id);
      toast.success("Indicator deactivated");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete");
    }
  };

  return (
    <Card data-testid="proficiency-catalog-manager">
      <CardHeader>
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <CardTitle>Proficiency Indicators</CardTitle>
            <CardDescription>
              Master catalog of long-term proficiencies and transferable skills.
              Set per-indicator minimum demonstrations and (optionally) link to assessment data for auto-promotion.
            </CardDescription>
          </div>
          <div className="flex items-end gap-2">
            <div>
              <Label className="text-xs">Subject</Label>
              <Select value={filterSubject} onValueChange={setFilterSubject}>
                <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All subjects</SelectItem>
                  {subjects.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Type</Label>
              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All types</SelectItem>
                  <SelectItem value="academic">Academic</SelectItem>
                  <SelectItem value="transferable">Transferable</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button
              onClick={() => setEditing({ isNew: true, subject: "", category: "", proficiency: "", indicator_label: "", indicator_text: "", type: "academic", min_demonstrations: 2, assessment_link: null })}
              className="bg-[#00305E] hover:bg-[#00305E]/90"
              data-testid="add-indicator-btn"
            >+ Add Indicator</Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <p className="text-center py-8 text-muted-foreground">Loading…</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/30 text-left">
                  <th className="px-3 py-2">Subject / Category</th>
                  <th className="px-3 py-2">Indicator</th>
                  <th className="px-3 py-2">Type</th>
                  <th className="px-3 py-2">Min</th>
                  <th className="px-3 py-2">Assessment Link</th>
                  <th className="px-3 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((ind) => (
                  <tr key={ind.id} className="border-b hover:bg-muted/10" data-testid={`indicator-row-${ind.id}`}>
                    <td className="px-3 py-2 text-xs">
                      <div className="font-medium">{ind.subject}</div>
                      {ind.category && <div className="text-muted-foreground">{ind.category}</div>}
                    </td>
                    <td className="px-3 py-2 max-w-md">
                      {ind.indicator_label && <span className="font-semibold mr-1">{ind.indicator_label}:</span>}
                      <span className="text-xs">{ind.indicator_text}</span>
                    </td>
                    <td className="px-3 py-2">
                      <Badge variant={ind.type === "transferable" ? "default" : "secondary"} className="text-[10px]">
                        {ind.type}
                      </Badge>
                    </td>
                    <td className="px-3 py-2 font-mono text-xs">{ind.min_demonstrations || 2}</td>
                    <td className="px-3 py-2 text-xs">
                      {ind.assessment_link?.category ? (
                        <span className="text-green-700">
                          {ind.assessment_link.category} ≥ {ind.assessment_link.min_score} × {ind.assessment_link.required_count}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right space-x-1">
                      <Button size="sm" variant="outline" onClick={() => setEditing({ ...ind, isNew: false })} data-testid={`edit-indicator-btn-${ind.id}`}>Edit</Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button size="sm" variant="destructive" data-testid={`delete-indicator-btn-${ind.id}`}>Delete</Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Deactivate indicator?</AlertDialogTitle>
                            <AlertDialogDescription>
                              This will hide the indicator from new entry but preserve historical records.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={() => handleDelete(ind.id)}>Deactivate</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr><td colSpan={6} className="text-center py-6 text-muted-foreground text-xs">No indicators match the filters.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>

      {editing && (
        <IndicatorEditor
          editing={editing}
          categories={categories}
          subjects={subjects}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
      <ProficiencyImportCard />
    </Card>
  );
}

function IndicatorEditor({ editing, categories, subjects, onClose, onSaved }) {
  const [form, setForm] = useState({
    subject: editing.subject || "",
    category: editing.category || "",
    proficiency: editing.proficiency || "",
    indicator_label: editing.indicator_label || "",
    indicator_text: editing.indicator_text || "",
    type: editing.type || "academic",
    min_demonstrations: editing.min_demonstrations || 2,
  });
  const [link, setLink] = useState(editing.assessment_link || { ...EMPTY_LINK });
  const [linkEnabled, setLinkEnabled] = useState(!!editing.assessment_link?.category);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!form.indicator_text.trim()) {
      toast.error("Indicator text is required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        min_demonstrations: Number(form.min_demonstrations) || 2,
        assessment_link: linkEnabled && link.category ? {
          category: link.category,
          min_score: Number(link.min_score) || 4,
          required_count: Number(link.required_count) || 3,
          group_type: link.group_type || null,
        } : null,
      };
      if (editing.isNew) {
        await createProficiencyIndicator(payload);
        toast.success("Indicator created");
      } else {
        await updateProficiencyIndicator(editing.id, payload);
        toast.success("Indicator updated");
      }
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editing.isNew ? "New Indicator" : "Edit Indicator"}</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Subject</Label>
            <Input value={form.subject} onChange={(e) => setForm((p) => ({ ...p, subject: e.target.value }))} placeholder="e.g., ELA" data-testid="ind-subject-input" list="subject-suggestions" />
            <datalist id="subject-suggestions">{subjects.map((s) => <option key={s} value={s} />)}</datalist>
          </div>
          <div className="space-y-2">
            <Label>Type</Label>
            <Select value={form.type} onValueChange={(v) => setForm((p) => ({ ...p, type: v }))}>
              <SelectTrigger data-testid="ind-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="academic">Academic</SelectItem>
                <SelectItem value="transferable">Transferable Skill</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Category (optional)</Label>
            <Input value={form.category} onChange={(e) => setForm((p) => ({ ...p, category: e.target.value }))} placeholder="e.g., Reading" data-testid="ind-category-input" />
          </div>
          <div className="space-y-2">
            <Label>Label (optional, e.g., "Indicator 1")</Label>
            <Input value={form.indicator_label} onChange={(e) => setForm((p) => ({ ...p, indicator_label: e.target.value }))} data-testid="ind-label-input" />
          </div>
          <div className="md:col-span-2 space-y-2">
            <Label>Proficiency (optional broader statement)</Label>
            <Input value={form.proficiency} onChange={(e) => setForm((p) => ({ ...p, proficiency: e.target.value }))} data-testid="ind-prof-input" />
          </div>
          <div className="md:col-span-2 space-y-2">
            <Label>Indicator Text *</Label>
            <textarea
              value={form.indicator_text} onChange={(e) => setForm((p) => ({ ...p, indicator_text: e.target.value }))}
              className="w-full min-h-[80px] rounded-md border bg-background p-2 text-sm"
              required data-testid="ind-text-input"
            />
          </div>
          <div className="space-y-2">
            <Label>Min Demonstrations (default threshold)</Label>
            <Input
              type="number" min={1}
              value={form.min_demonstrations}
              onChange={(e) => setForm((p) => ({ ...p, min_demonstrations: e.target.value }))}
              data-testid="ind-min-input"
            />
            <p className="text-xs text-muted-foreground">Number of times the student must hit "3" to be Achieved. Can be overridden per-student.</p>
          </div>
        </div>

        <div className="border-t pt-4 space-y-3">
          <div className="flex items-center justify-between">
            <Label className="text-base">Assessment Auto-Link (optional)</Label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={linkEnabled}
                onChange={(e) => setLinkEnabled(e.target.checked)}
                data-testid="link-enabled-toggle"
              />
              Enable
            </label>
          </div>
          {linkEnabled && (
            <div className="rounded-md border bg-muted/20 p-3 space-y-3">
              <p className="text-xs text-muted-foreground">
                When a student hits a score of <strong>≥ Min Score</strong> in this assessment category at least <strong>Required Hits</strong> times within the school year, this indicator will auto-mark as Achieved.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Assessment Category</Label>
                  <Select value={link.category} onValueChange={(v) => setLink((p) => ({ ...p, category: v }))}>
                    <SelectTrigger data-testid="link-category-select"><SelectValue placeholder="Choose…" /></SelectTrigger>
                    <SelectContent>
                      {categories.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Min Score (1-4)</Label>
                  <Input
                    type="number" min={1} max={4}
                    value={link.min_score}
                    onChange={(e) => setLink((p) => ({ ...p, min_score: e.target.value }))}
                    data-testid="link-min-score-input"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Required Hits</Label>
                  <Input
                    type="number" min={1}
                    value={link.required_count}
                    onChange={(e) => setLink((p) => ({ ...p, required_count: e.target.value }))}
                    data-testid="link-required-input"
                  />
                </div>
                <div className="space-y-1 md:col-span-3">
                  <Label className="text-xs">Limit to Group/Class (optional)</Label>
                  <Input
                    value={link.group_type || ""}
                    onChange={(e) => setLink((p) => ({ ...p, group_type: e.target.value }))}
                    placeholder="e.g., Art Block 1 — leave blank for any class"
                    data-testid="link-group-input"
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving} className="bg-[#00305E] hover:bg-[#00305E]/90" data-testid="ind-save-btn">
            {saving ? "Saving…" : (editing.isNew ? "Create" : "Save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
